"use client";

import type { Map as LeafletMap } from "leaflet";
import { useEffect, useRef } from "react";
import { sendGTMEvent } from "@next/third-parties/google";
import "leaflet/dist/leaflet.css";
import type { Location } from "./location-types";

const DEFAULT_CENTER: [number, number] = [29.854533022726017, -95.56246222076811];
const DEFAULT_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const DEFAULT_TILE_ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

function createPopupContent(location: Location): HTMLDivElement {
    const container = document.createElement("div");
    container.className = "t-min-w-48 t-p-1";

    const heading = document.createElement("h3");
    heading.className = "t-text-base t-font-semibold t-text-brand-violet-dark";
    heading.textContent = location.name;
    container.appendChild(heading);

    const address = document.createElement("p");
    address.className = "t-mt-1 t-text-sm t-text-gray-700";
    address.textContent = [
        location.address,
        [location.city, location.state].filter(Boolean).join(", "),
        location.zip
    ]
        .filter(Boolean)
        .join(" ");
    container.appendChild(address);

    if (location.phone) {
        const phoneLink = document.createElement("a");
        phoneLink.className = "t-mt-2 t-inline-block t-font-semibold t-text-brand-violet-dark t-hover:text-purple-900";
        phoneLink.href = `tel:${location.phone.replace(/[^\d+]/g, "")}`;
        phoneLink.textContent = location.phone;
        phoneLink.addEventListener("click", () => {
            sendGTMEvent({ event: "click_to_call", page_section: "locations_map" });
        });
        container.appendChild(phoneLink);
    }

    if (location.googleMapsLink) {
        const directionsLink = document.createElement("a");
        directionsLink.className = "t-mt-2 t-block t-text-sm t-font-semibold t-text-automotive-secondary t-hover:text-automotive-primary";
        directionsLink.href = location.googleMapsLink;
        directionsLink.target = "_blank";
        directionsLink.rel = "noopener noreferrer";
        directionsLink.textContent = "Get directions";
        directionsLink.addEventListener("click", () => {
            sendGTMEvent({ event: "click_directions", page_section: "locations_map", interaction_outcome: "intent" });
        });
        container.appendChild(directionsLink);
    }

    return container;
}

interface LocationsMapSectionProps {
    locations: Location[];
    variant?: "default" | "hero";
}

export default function LocationsMapSection({
    locations,
    variant = "default"
}: LocationsMapSectionProps) {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let map: LeafletMap | undefined;
        let cancelled = false;

        async function initializeMap() {
            if (!containerRef.current) return;

            const leaflet = await import("leaflet");
            if (cancelled || !containerRef.current) return;

            const L = leaflet.default;
            const validLocations = locations.filter(
                (location) =>
                    Number.isFinite(location.geoLocation?.lat) && Number.isFinite(location.geoLocation?.lng)
            );

            map = L.map(containerRef.current, {
                center: DEFAULT_CENTER,
                zoom: 9,
                zoomControl: variant !== "hero",
                attributionControl: variant !== "hero",
                scrollWheelZoom: variant !== "hero"
            });

            L.tileLayer(process.env["NEXT_PUBLIC_MAP_TILE_URL"] || DEFAULT_TILE_URL, {
                attribution: process.env["NEXT_PUBLIC_MAP_TILE_ATTRIBUTION"] || DEFAULT_TILE_ATTRIBUTION,
                maxZoom: 19
            }).addTo(map);

            const bounds = L.latLngBounds([]);

            validLocations.forEach((location) => {
                const position: [number, number] = [location.geoLocation.lat, location.geoLocation.lng];
                const icon = L.icon({
                    iconUrl: location.comingSoon ? "/media/tc-cs-pin.png" : "/media/tc-pin.png",
                    iconSize: [64, 64],
                    iconAnchor: [32, 64],
                    popupAnchor: [0, -58]
                });

                L.marker(position, {
                    icon,
                    title: location.name,
                    alt: location.name
                })
                    .addTo(map as LeafletMap)
                    .bindPopup(createPopupContent(location), { maxWidth: 320 });

                bounds.extend(position);
            });

            if (bounds.isValid()) {
                const isDesktopHero = variant === "hero" && containerRef.current.clientWidth >= 1024;

                map.fitBounds(
                    bounds,
                    isDesktopHero
                        ? {
                              paddingTopLeft: [
                                  Math.min(containerRef.current.clientWidth * 0.43, 720),
                                  48
                              ],
                              paddingBottomRight: [48, 48],
                              maxZoom: 10
                          }
                        : {
                              padding: variant === "hero" ? [32, 32] : [36, 36],
                              maxZoom: variant === "hero" ? 9 : 11
                          }
                );
            }

            requestAnimationFrame(() => map?.invalidateSize());
        }

        void initializeMap();

        return () => {
            cancelled = true;
            map?.remove();
        };
    }, [locations, variant]);

    return (
        <div
            ref={containerRef}
            className="t-h-full t-w-full"
            role="region"
            aria-label="Map of THIScar locations across Greater Houston"
        />
    );
}
